import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Col } from "react-bootstrap";
import "components/custom.css";
import reactStringReplace from 'react-string-replace';
import ReactHtmlParser from 'react-html-parser';
import WordDefinitionModal from 'components/PracticeMaterial/PopUps/WordDefinitionModal';
import FillInTheBlankExplanation from "components/PracticeMaterial/PopUps/FillInTheBlankExplanation";
import TimeUp from "components/PracticeMaterial/PopUps/TimeUp";

const IDEAL_TIME = 120; // SECONDS.

const ReadingBlank = (props) => {
  // Optimized: Combined related state
  const [componentState, setComponentState] = useState({
    questionId: "",
    reset: false,
    correctFlag: true,
    resultObj: {},
    answerData: {
      answer: [],
      correct: [],
      selectedAnswer: ""
    },
    wordDefinitionModalIsOpen: false,
    modalDestroy: true,
    selectedWord: "",
    isModalOpen: false,
    answer_explaination_key: "",
    explanationModal: false,
    idealTime: IDEAL_TIME,
    idealTimeExceed: false,
    draggedElement: null,
    touchStartPos: { x: 0, y: 0 },
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    lastTapTime: 0
  });

  const [optionList, setOptionList] = useState([]);

  // Refs for DOM elements and intervals
  const questionPara = useRef([]);
  const dragPreviewRef = useRef(null);
  const idealTimeIntervalRef = useRef(null);
  const dragStartTimeoutRef = useRef(null);
  const blankElementsRef = useRef(new Map());
  const optionElementsRef = useRef(new Map());

  // Optimized: Memoized constants
  const { answer, showResponse, resultMode, testMode, data, restart } = props;
  const isAnswered = useMemo(() => !!answer, [answer]);

  // Stop timer when answer is submitted
  useEffect(() => {
    if (answer && idealTimeIntervalRef.current) {
      clearInterval(idealTimeIntervalRef.current);
      idealTimeIntervalRef.current = null;
    }
  }, [answer]);

  // Optimized: Single useEffect for initial setup
  useEffect(() => {
    // Start countdown
    idealTimeIntervalRef.current = setInterval(countDownToIdealTime, 1000);

    // Add CSS for drop target highlighting
    const style = document.createElement('style');
    style.textContent = `
      .span1.drop-target-highlight {
        background-color: #e3f2fd !important;
        border: 2px dashed #2196f3 !important;
        box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2) !important;
      }
      
      .dragging-ghost {
        transition: transform 0.1s ease;
      }
      
      .span2.dragging {
        opacity: 0.4;
        transform: scale(0.95);
      }
    `;
    style.id = 'reading-blank-styles';
    document.head.appendChild(style);

    return () => {
      // Cleanup
      if (idealTimeIntervalRef.current) {
        clearInterval(idealTimeIntervalRef.current);
      }
      const styleEl = document.getElementById('reading-blank-styles');
      if (styleEl) {
        document.head.removeChild(styleEl);
      }
      if (dragStartTimeoutRef.current) {
        clearTimeout(dragStartTimeoutRef.current);
      }

      // Cleanup event listeners
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  // Optimized: Combined event listeners effect
  useEffect(() => {
    const { isDragging } = componentState;

    if (isDragging) {
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);
      document.addEventListener('touchcancel', handleGlobalTouchEnd);

      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }

    return () => {
      if (isDragging) {
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
        document.removeEventListener('touchcancel', handleGlobalTouchEnd);
      }
    };
  }, [componentState.isDragging]);

  // Optimized: Data and props effect
  useEffect(() => {
    props.restartQue();
    if (data) {
      if ((componentState.questionId && componentState.questionId !== data.id) || restart) {
        resetUI();
        setComponentState(prev => ({
          ...prev,
          correctFlag: true,
          questionId: data.id
        }));
        setOptionList(data.option || []);
      } else if (!componentState.questionId) {
        setComponentState(prev => ({
          ...prev,
          correctFlag: true,
          questionId: data.id
        }));
        setOptionList(data.option || []);
      }
    }
  }, [data, restart, componentState.questionId]);

  // Optimized: Update correct answers
  useEffect(() => {
    if (data && componentState.correctFlag) {
      updateCorrect();
    }
  }, [data, componentState.correctFlag]);

  // Optimized: Helper function to update state
  const updateComponentState = useCallback((updates) => {
    setComponentState(prev => ({ ...prev, ...updates }));
  }, []);

  // Optimized: Timer function
  const countDownToIdealTime = useCallback(() => {
    if (isAnswered && idealTimeIntervalRef.current) {
      clearInterval(idealTimeIntervalRef.current);
      idealTimeIntervalRef.current = null;
      return;
    }

    setComponentState(prev => {
      if (prev.idealTime > 0) {
        return { ...prev, idealTime: prev.idealTime - 1 };
      } else {
        if (idealTimeIntervalRef.current) {
          clearInterval(idealTimeIntervalRef.current);
          idealTimeIntervalRef.current = null;
        }
        return { ...prev, idealTimeExceed: true };
      }
    });
  }, [isAnswered]);

  // Optimized: Reset UI function
  const resetUI = useCallback(() => {
    // Use refs for better performance
    blankElementsRef.current.forEach((_, key) => {
      const el = document.getElementById(key);
      if (el && el.classList.contains('span1')) {
        el.innerHTML = "";
      }
    });

    optionElementsRef.current.forEach((txt, key) => {
      const el = document.getElementById(key);
      if (el && el.classList.contains('span2')) {
        el.style.display = "inline-block";
        el.innerHTML = txt;
      }
    });
  }, []);

  // Optimized: Touch handlers
  const handleTouchStart = useCallback((e, elementId) => {
    if (isAnswered) return;

    // Comment out because of error, in terminal
    // e.preventDefault();
    const touch = e.touches[0];
    const element = document.getElementById(elementId);
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;

    updateComponentState({
      touchStartPos: { x: touch.clientX, y: touch.clientY },
      dragOffset: { x: offsetX, y: offsetY },
      draggedElement: elementId
    });

    // Visual feedback
    element.style.opacity = '0.4';
    element.style.transform = 'scale(0.95)';

    // Create drag preview
    createDragPreview(element, touch.clientX, touch.clientY);

    // Delay to distinguish tap from drag
    dragStartTimeoutRef.current = setTimeout(() => {
      updateComponentState({ isDragging: true });
    }, 50);
  }, [isAnswered, updateComponentState]);

  // Optimized: Drag preview functions
  const createDragPreview = useCallback((element, x, y) => {
    removeDragPreview();

    const ghost = element.cloneNode(true);
    const { dragOffset } = componentState;

    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${x - dragOffset.x}px`,
      top: `${y - dragOffset.y}px`,
      zIndex: '9999',
      pointerEvents: 'none',
      opacity: '0.8',
      transform: 'scale(1.1)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      borderRadius: '4px',
      backgroundColor: '#fff',
      padding: '8px 12px',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      color: 'inherit'
    });

    ghost.className = 'span2 dragging-ghost';
    document.body.appendChild(ghost);
    dragPreviewRef.current = ghost;
  }, [componentState.dragOffset]);

  const updateDragPreview = useCallback((x, y) => {
    if (dragPreviewRef.current) {
      dragPreviewRef.current.style.left = `${x - componentState.dragOffset.x}px`;
      dragPreviewRef.current.style.top = `${y - componentState.dragOffset.y}px`;
    }
  }, [componentState.dragOffset]);

  const removeDragPreview = useCallback(() => {
    if (dragPreviewRef.current) {
      document.body.removeChild(dragPreviewRef.current);
      dragPreviewRef.current = null;
    }
  }, []);

  // Optimized: Global touch handlers
  const handleGlobalTouchMove = useCallback((e) => {
    if (!componentState.isDragging || !componentState.draggedElement) return;

    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    updateDragPreview(touch.clientX, touch.clientY);

    const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);
    highlightDropTarget(elementAtPoint);
  }, [componentState.isDragging, componentState.draggedElement, updateDragPreview]);



  // Optimized: Gather answers
  const gatherAnswer = useCallback(() => {
    const container = document.querySelector('.response');
    if (!container) return [];

    const blanks = container.querySelectorAll(".span1");
    const values = Array(blanks.length * 2).fill(undefined);

    blanks.forEach((blank, index) => {
      values[(index * 2) + 1] = blank.innerHTML;
    });

    return values;
  }, []);


  // Optimized: Perform drop
  const performDrop = useCallback((sourceId, targetElement) => {
    if (!data) return;

    props.showResult?.();

    const correct_key_data = data.question.replace(/<[^>]*>?/gm, 'x_x').split('x_x')
      .filter((_, x) => x % 2 !== 0);

    const sourceElement = document.getElementById(sourceId);
    if (!sourceElement) return;

    const temp = targetElement.innerHTML;
    const key = targetElement.getAttribute("no");
    const newAnswerData = [...componentState.answerData.answer];

    if (temp === "") {
      targetElement.innerHTML = sourceElement.innerHTML;
      sourceElement.innerHTML = temp;
      newAnswerData[key] = targetElement.innerHTML;
    } else {
      targetElement.innerHTML = sourceElement.innerHTML;
      sourceElement.innerHTML = temp;
      newAnswerData[key] = targetElement.innerHTML;
    }

    const prepared_data = {
      answer: gatherAnswer(),
      correct: correct_key_data
    };

    props.submit?.(prepared_data, true);

    // ✅ FIX: Reset drag state immediately after drop
    updateComponentState({
      resultObj: prepared_data,
      answerData: prepared_data,
      isDragging: false,
      draggedElement: null,
      touchStartPos: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 }
    });

    // ✅ FIX: Cleanup timeout
    if (dragStartTimeoutRef.current) {
      clearTimeout(dragStartTimeoutRef.current);
      dragStartTimeoutRef.current = null;
    }
  }, [data, componentState.answerData.answer, props, updateComponentState, gatherAnswer]);


  const handleGlobalTouchEnd = useCallback((e) => {
    // ✅ FIX: Always cleanup, even if not dragging
    if (!componentState.isDragging || !componentState.draggedElement) {
      // Cleanup any leftover state
      updateComponentState({
        isDragging: false,
        draggedElement: null,
        touchStartPos: { x: 0, y: 0 },
        dragOffset: { x: 0, y: 0 }
      });

      removeDragPreview();
      document.querySelectorAll('.span1.drop-target-highlight').forEach(el => {
        el.classList.remove('drop-target-highlight');
      });

      return;
    }

    const touch = e.changedTouches[0];
    const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);

    // Reset dragged element
    const draggedElem = document.getElementById(componentState.draggedElement);
    if (draggedElem) {
      draggedElem.style.opacity = '';
      draggedElem.style.transform = '';
    }

    // Cleanup
    document.querySelectorAll('.span1.drop-target-highlight').forEach(el => {
      el.classList.remove('drop-target-highlight');
    });

    removeDragPreview();

    // Find drop target
    let dropTarget = null;
    if (elementAtPoint) {
      if (elementAtPoint.classList.contains('span1')) {
        dropTarget = elementAtPoint;
      } else {
        const parentSpan1 = elementAtPoint.closest('.span1');
        dropTarget = parentSpan1;
      }
    }

    // Perform drop
    if (dropTarget && dropTarget.classList.contains('span1')) {
      performDrop(componentState.draggedElement, dropTarget);
    }

    updateComponentState({
      isDragging: false,
      draggedElement: null,
      touchStartPos: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 }
    });

    if (dragStartTimeoutRef.current) {
      clearTimeout(dragStartTimeoutRef.current);
      dragStartTimeoutRef.current = null;
    }


    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }, [componentState.isDragging, componentState.draggedElement, updateComponentState, removeDragPreview, performDrop]);

  // Optimized: Drop target highlighting
  const highlightDropTarget = useCallback((element) => {
    // Remove old highlights
    const currentHighlights = document.querySelectorAll('.span1.drop-target-highlight');
    currentHighlights.forEach(el => el.classList.remove('drop-target-highlight'));

    // Find new target
    let span1Element = null;
    if (element) {
      span1Element = element.classList.contains('span1')
        ? element
        : element.closest('.span1');
    }

    // Add highlight
    if (span1Element && !span1Element.classList.contains('correct') && !span1Element.classList.contains('wrong')) {
      span1Element.classList.add('drop-target-highlight');
    }
  }, []);


  // Optimized: Update correct
  const updateCorrect = useCallback(() => {
    if (!data) return;

    const correctAnswers = data.question.replace(/<[^>]*>?/gm, 'x_x').split('x_x')
      .filter((_, x) => x % 2 !== 0);

    props.submit?.({ 'answer': [], 'correct': correctAnswers });

    updateComponentState({
      correctFlag: false,
      resultObj: { 'answer': [], 'correct': correctAnswers }
    });
  }, [data, props, updateComponentState]);

  // Optimized: Drag event handlers (for desktop)
  const allowDrop = useCallback((event) => {
    event.preventDefault();
  }, []);

  const dragStart = useCallback((event) => {
    event.dataTransfer.setData("Text", event.target.id);
  }, []);

  // Optimized: Drop handler (for desktop)
  const drop1 = useCallback((event) => {
    if (!data) return;

    props.showResult?.();
    event.preventDefault();

    const dataTransfer = event.dataTransfer.getData("Text");
    const correct_key_data = data.question.replace(/<[^>]*>?/gm, 'x_x').split('x_x')
      .filter((_, x) => x % 2 !== 0);

    const sourceElement = document.getElementById(dataTransfer);
    if (!sourceElement) return;

    const temp = event.target.innerHTML;
    const key = event.target.getAttribute("no");
    const newAnswerData = [...componentState.answerData.answer];

    if (temp === "") {
      event.target.innerHTML = sourceElement.innerHTML;
      sourceElement.innerHTML = temp;
      newAnswerData[key] = event.target.innerHTML;
    } else {
      event.target.innerHTML = sourceElement.innerHTML;
      sourceElement.innerHTML = temp;
      newAnswerData[key] = event.target.innerHTML;
    }

    const prepared_data = {
      answer: gatherAnswer(),
      correct: correct_key_data
    };

    props.submit?.(prepared_data, true);

    updateComponentState({
      resultObj: prepared_data,
      answerData: prepared_data
    });
  }, [data, componentState.answerData.answer, props, gatherAnswer, updateComponentState]);

  // Optimized: Modal handlers
  const openCloseWordDefinitionModal = useCallback((status, word = "") => {
    updateComponentState({ wordDefinitionModalIsOpen: status });

    if (!status) {
      setTimeout(() => {
        updateComponentState({
          modalDestroy: true,
          selectedWord: ""
        });
      }, 500);
    } else {
      updateComponentState({
        modalDestroy: false,
        selectedWord: word
      });
    }
  }, [updateComponentState]);

  const wordClickHandle = useCallback((e, word) => {
    e.preventDefault();
    openCloseWordDefinitionModal(true, word);
  }, [openCloseWordDefinitionModal]);

  const toggleModalExplanation = useCallback(() => {
    setComponentState(prev => ({
      ...prev,
      explanationModal: !prev.explanationModal
    }));
  }, [updateComponentState]);

  const idealTimeExceedToggle = useCallback(() => {
    setComponentState(prev => ({
      ...prev,
      idealTimeExceed: !prev.idealTimeExceed
    }));
  }, [updateComponentState]);

  // Optimized: Answer status class
  const answerStatusClass = useCallback((item, correct) => {
    if (!isAnswered) return "";

    const { answerData } = componentState;
    const user_selected = answerData.answer[item]?.trim() || answerData.answer[item];

    if (user_selected === "" || user_selected === undefined) {
      return "empty cursor-pointer clearfix text-decoration-none";
    } else if (user_selected === correct.trim()) {
      return "correct";
    } else {
      return "wrong cursor-pointer clearfix text-decoration-none";
    }
  }, [isAnswered, componentState.answerData]);

  // Optimized: Blank click handler
  const blankClickHandler = useCallback((e, correct, item) => {
    if (!isAnswered) return;

    const { answerData } = componentState;
    const user_selected = answerData.answer[item]?.trim() || answerData.answer[item];

    if (user_selected !== correct.trim()) {
      updateComponentState({ answer_explaination_key: correct });
      setTimeout(toggleModalExplanation, 0);
    }
  }, [isAnswered, componentState.answerData, updateComponentState, toggleModalExplanation]);

  // Optimized: Fill blanks
  const fillBlanks = useCallback((item) => {
    const { answerData } = componentState;
    const { answer: userAnswer, correct } = answerData;

    if (userAnswer[item] === "") {
      const correctAnswer = correct[Math.round(item / 2) - 1];
      return ReactHtmlParser(`${correctAnswer} <span className="pull-right">&nbsp;?</span>`);
    } else {
      if (correct[Math.round(item / 2) - 1] !== userAnswer[item]) {
        return ReactHtmlParser(`${userAnswer[item]} <span className="pull-right">&nbsp;?</span>`);
      }
      return userAnswer[item];
    }
  }, [componentState.answerData]);

  // Optimized: Show correct with wrong
  const showCorrectAlongWithWrong = useCallback((item, correct) => {
    if (!isAnswered) return "";

    const { answerData } = componentState;
    const user_selected = answerData.answer[item]?.trim() || answerData.answer[item];

    if (!user_selected || user_selected === "" || user_selected === correct.trim()) {
      return "";
    }

    return (
      <span className="wrong-answer-correction">
        <i className="fa fa-long-arrow-right"></i>
        <span className="span11 correct-inverse text-center py-2 text-black">{correct.trim()}</span>
      </span>
    );
  }, [isAnswered, componentState.answerData]);

  // Optimized: Render question paragraph
  const renderQuestionParagraph = useMemo(() => {
    if (!questionPara.current || questionPara.current.length === 0) return null;

    return questionPara.current.map((item, i) => {
      if (typeof item === "string") {
        const words = item.split(/(\s+)/);
        return words.map((word, wordIndex) => {
          if (word.trim()) {
            return (
              <span
                key={`${i}-${wordIndex}`}
                className="question-para-word speach-text select-word-hover-effect"
                onClick={(e) => wordClickHandle(e, word.trim())}
              >
                {word}
              </span>
            );
          } else if (word === " ") {
            return ' ';
          }
          return null;
        });
      } else if (React.isValidElement(item)) {
        return React.cloneElement(item, { key: i });
      }
      return <React.Fragment key={i}>{item}</React.Fragment>;
    });
  }, [questionPara.current, wordClickHandle]);


  const handleDoubleTap = (e, i) => {
    const currentTime = Date.now();
    const tapGap = currentTime - componentState.lastTapTime;

    if (tapGap < 300) { 
      handleBlankClick(e, i);
    }
    setComponentState(prev => ({ ...prev, lastTapTime: currentTime }));
  };

  // Helper function: Find original option ID
  const findOriginalOptionId = (content) => {
    // Look through option list to find matching content
    for (let i = 0; i < optionList.length; i++) {
      const optionId = `item-${optionList[i].options.trim()}`;
      const optionElement = document.getElementById(optionId);

      if (optionElement && optionElement.innerHTML.trim() === "") {
        // This is the empty option that matches our content
        return optionId;
      }
    }
    return null;
  };

  const updateAnswerAfterRemoval = (blankIndex) => {
    const correct_key_data = props.data.question
      .replace(/<[^>]*>?/gm, "x_x")
      .split("x_x")
      .filter(function (i, x) {
        if (x % 2 != 0) return i;
      });

    const prepared_data = {
      answer: gatherAnswer(),
      correct: correct_key_data,
    };

    props.submit(prepared_data, true);
  };
  // Back to original option when blank is clicked
  const handleBlankClick = (e, blankIndex) => {
    if (props.answer) return; // Don't allow if answer is submitted

    const blankElement = e.target;
    const blankContent = blankElement.innerHTML.trim();

    // Only proceed if blank has content
    if (blankContent === "") return;

    // Find the original option element to restore content
    const originalOptionId = findOriginalOptionId(blankContent);
    if (originalOptionId) {
      // Clear the blank
      blankElement.innerHTML = "";

      // Restore content to original option
      const originalOption = document.getElementById(originalOptionId);
      if (originalOption) {
        originalOption.innerHTML = blankContent;
        originalOption.style.display = "inline-block";
      }

      // Update answer data
      updateAnswerAfterRemoval(blankIndex);
    }
  };
  // Optimized: Process question data
  useMemo(() => {
    if (data) {
      const questionText = data.question;
      const correct = questionText.replace(/<[^>]*>?/gm, 'x_x').split('x_x')
        .filter((_, x) => x % 2 !== 0);

      // Clear refs
      blankElementsRef.current.clear();
      optionElementsRef.current.clear();

      const processedQuestion = reactStringReplace(
        questionText.replace(/<[^>]*>?/gm, 'x_x').replace(/x_x(.*?)x_x/g, ''),
        /(__add_blank__)/g,
        (match, i) => {
          const blankId = `item-${i}`;
          blankElementsRef.current.set(blankId, correct[(i - 1) / 2]);

          return !isAnswered ? (
            <span
              key={blankId}
              className={`span1 ${answerStatusClass(i, correct[(i - 1) / 2])}`}
              onDrop={drop1}
              draggable={isAnswered ? "false" : "true"}
              onDragStart={dragStart}
              no={i}
              id={blankId}
              onDragOver={allowDrop}
              onClick={(e) => blankClickHandler(e, correct[(i - 1) / 2], i)}
              onDoubleClick={(e) => handleBlankClick(e, i)}
              onTouchStart={(e) => handleDoubleTap(e, i)}
              onTouchEnd={(e) => {
                if (componentState.draggedElement) {
                  handleGlobalTouchEnd(e);
                }
              }}
            ></span>
          ) : (
            <React.Fragment key={blankId}>
              <span
                className={`span1 ${answerStatusClass(i, correct[(i - 1) / 2])}`}
                onClick={(e) => blankClickHandler(e, correct[(i - 1) / 2], i)}
              >
                {fillBlanks(i)}
              </span>
              {showCorrectAlongWithWrong(i, correct[(i - 1) / 2])}
            </React.Fragment>
          );
        }
      );

      questionPara.current = processedQuestion;

      // Store option elements
      if (data.option) {
        data.option.forEach((item, i) => {
          const optionId = `item-${item.options.trim()}`;
          optionElementsRef.current.set(optionId, item.options.trim());
        });
      }
    }
  }, [data, isAnswered, answerStatusClass, drop1, dragStart, allowDrop,
    blankClickHandler, fillBlanks, showCorrectAlongWithWrong,
    componentState.draggedElement, handleGlobalTouchEnd]);

  // Commented out legacy code that can be removed:
  /*
  // Original showAnswer function - can be removed if not used elsewhere
  const showAnswer = () => {
    if (!props.resultMode) {
      let elem = document.querySelectorAll('.checkedSign');
      for (var i = 0; i <= elem.length - 1; i++) {
        let id = elem[i].dataset.id;

        if (elem[i].dataset.text.trim() === document.getElementById('item-' + id).textContent.trim()) {
          elem[i].innerHTML = '<i className="fa fa-check" style="font-size:32px;color:green"></i>';
        } else {
          elem[i].innerHTML = `
          <i className="fa fa-times" style="font-size:32px;color:red"></i>
        `;
        }
      }
    }
  };

  // Original stopTest function - can be removed if not used
  const stopTest = () => {
    props.nextQuestion(componentState.resultObj);
  };

  // Original drop function - simplified version kept
  const drop = (event) => {
    event.preventDefault();
    var data = event.dataTransfer.getData("Text");
    var temp = event.target.innerHTML;
    event.target.innerHTML = document.getElementById(data).innerHTML;
    document.getElementById(data).innerHTML = temp;
  };

  // Original onDrop function - can be removed
  const onDrop = (data) => {
    console.log(data);
  };

  // Original toggleModal function - can be removed if not used
  const toggleModal = () => {
    updateComponentState(prev => ({ ...prev, isModalOpen: !prev.isModalOpen }));
  };
  */

  // Simplified drop function for options
  const drop = useCallback((event) => {
    event.preventDefault();

    const dragData = event.dataTransfer.getData("Text");
    const temp = event.target.innerHTML;
    const sourceElement = document.getElementById(dragData);
    if (sourceElement) {
      event.target.innerHTML = sourceElement.innerHTML;
      sourceElement.innerHTML = temp;
    }
    const correct_key_data = data.question.replace(/<[^>]*>?/gm, 'x_x').split('x_x')
      .filter((_, x) => x % 2 !== 0);

    // Check if dropping back to the same element
    if (sourceElement !== event.target) {
      const prepared_data = {
        answer: gatherAnswer(),
        correct: correct_key_data
      };
      props.submit(prepared_data, true);
      return;
    }
  }, [data, gatherAnswer, props]);

  // Render optimization: Memoized option list
  const renderedOptions = useMemo(() => {
    if (!data || resultMode || !optionList.length) return null;

    return optionList.map((item, i) => {
      const itemId = `item-${item.options.trim()}`;
      return (
        <li key={i} className="pb-2 px-1">
          <span
            draggable={isAnswered ? "false" : "true"}
            no="0"
            onDrop={drop}
            onDragOver={allowDrop}
            onDragStart={dragStart}
            onTouchStart={(e) => handleTouchStart(e, itemId)}
            // onTouchMove={(e) => e.preventDefault()}
            // onTouchEnd={(e) => e.preventDefault()}
            className="span2"
            txt={item.options.trim()}
            id={itemId}
            style={{
              touchAction: 'none',
              cursor: 'grab',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              minHeight: '35px'
            }}
          >
            {item.options.trim()}
          </span>
        </li>
      );
    });
  }, [data, resultMode, optionList, isAnswered, drop, allowDrop, dragStart, handleTouchStart]);

  // Reset handling (if needed)
  useEffect(() => {
    if (componentState.reset) {
      resetUI();
      setComponentState(prev => ({ ...prev, reset: false }));
    }
  }, [componentState.reset, resetUI]);

  // Show answer handling (if needed)
  useEffect(() => {
    if (isAnswered && !resultMode) {
      // Original showAnswer logic can be implemented here if needed
    }
  }, [isAnswered, resultMode]);

  return (
    <div className="visibility-visible" id="contentSection">
      {!resultMode && !testMode && (
        <Col xs={12}>
          <div className="row shadowing">
            <div className="word-definition">
              {/* {!componentState.modalDestroy && (
                <WordDefinitionModal
                  isOpen={componentState.wordDefinitionModalIsOpen}
                  toggle={openCloseWordDefinitionModal}
                  word={componentState.selectedWord}
                  languages={props.languages}
                />
              )} */}
            </div>
          </div>
        </Col>
      )}

      {componentState.explanationModal && (
        <FillInTheBlankExplanation
          isOpen={componentState.explanationModal}
          title={`${componentState.answer_explaination_key.charAt(0).toUpperCase()}${componentState.answer_explaination_key.slice(1)}`}
          data={props.option_explaination}
          keyName={componentState.answer_explaination_key}
          closeModal={toggleModalExplanation}
        />
      )}

      <TimeUp
        isOpen={componentState.idealTimeExceed}
        toggle={idealTimeExceedToggle}
        title="Time Alert"
        message="You spent 2 minutes on [Reading:Fill in the blanks], which is the recommended time. Click Submit Now to submit your response, or click on Cross to ignore."
        buttonLabel="Submit Now"
        showResponse={showResponse}
        submitDisable={props.submitDisable}
      />

      <div className="custom-box m-0">
        <div className="ice-storm-form text-decoration-none response question-text">
          {renderQuestionParagraph}
        </div>
      </div>

      <div className="custom-box m-0 mt-3">
        <ul className="ice-storm-list">
          {renderedOptions}
        </ul>
      </div>
    </div>
  );
};

export default ReadingBlank;